import type { DragEvent } from 'react';

import { CloudUpload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { toast } from 'sonner';

import type { FileTypes, FileTypesSubtype } from '../config/file';

import { Button } from '../components/button';
import { Skeleton } from '../components/skeleton';
import { makeAcceptString } from '../config/file';
import { FileSize } from '../lib/file-size';
import { cn, formatFileName } from '../lib/utils';
import {
    Popout,
    PopoutBody,
    PopoutClose,
    PopoutContent,
    PopoutDescription,
    PopoutFooter,
    PopoutHeader,
    PopoutTitle,
} from './popout';

export type FileProps = {
    file?: File | null;
    disabled?: boolean;
    iconNode?: React.ReactNode;
    previewerDisabled?: boolean;
};

export function File({ file, disabled = false, previewerDisabled = false, iconNode = null }: FileProps) {
    const { t } = useTranslation('common');

    const [open, setOpen] = useState(false);

    const fileName = useMemo(() => {
        if (!file)
            return null;

        return formatFileName(file?.name ?? 'Onbekend bestand');
    }, [file]);
    const fileSize = useMemo(() => (file ? FileSize(file?.size ?? 0) : null), [file]);

    const toggleOpen = () => setOpen(prev => !prev);

    return (
        <>
            <button
                type="button"
                className={cn(
                    'flex w-full items-center gap-2 rounded-md p-2',
                    'border border-input-border bg-transparent',
                    'transition duration-100',
                    'placeholder:font-normal placeholder:text-muted-foreground hover:border-input-border-hover hover:shadow-input-hover',
                    'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                disabled={disabled}
                onClick={toggleOpen}
            >
                <div className="flex flex-row gap-4 text-start">
                    {iconNode && (
                        iconNode
                    )}
                    <div className="flex flex-col gap-1 text-start">
                        {fileName
                            ? (
                                    <span>{fileName}</span>
                                )
                            : (
                                    <Skeleton className="h-5 w-2/3 min-w-20 bg-background-quaternary" />
                                )}
                        {fileSize
                            ? (
                                    <span className="text-xs text-gray-500">
                                        {fileSize}
                                    </span>
                                )
                            : (
                                    <Skeleton className="h-4 w-1/2 bg-background-quaternary" />
                                )}
                    </div>
                </div>
            </button>
            <Popout open={open && !previewerDisabled} onOpenChange={setOpen}>
                <PopoutContent
                    className="max-h-screen sm:w-full"
                >
                    <PopoutHeader className="flex flex-col gap-2">
                        <PopoutTitle>{fileName}</PopoutTitle>
                        <PopoutDescription>
                            {t('file.description', {
                                type: t(`file.types.${file?.type?.startsWith('image/') ? 'image' : 'all'}`),
                                size: fileSize,
                            })}
                        </PopoutDescription>
                    </PopoutHeader>
                    <PopoutBody className="h-full w-full">
                        {file
                            ? (
                                    <Previewer file={file} className="h-full w-full max-h-[70vh] max-w-[80vw] flex justify-center" />
                                )
                            : (
                                    <Skeleton className="h-full w-full bg-background-quaternary" />
                                )}
                    </PopoutBody>
                    <PopoutFooter>
                        <PopoutClose asChild>
                            <Button variant="ghost">{t('file.close')}</Button>
                        </PopoutClose>
                    </PopoutFooter>
                </PopoutContent>
            </Popout>
        </>
    );
}

function isAndroid(): boolean {
    if (typeof window === 'undefined')
        return false;
    return navigator.userAgent.includes('Android');
}

export function Previewer({
    file,
    className,
}: {
    file: File;
    className?: string;
}) {
    const { t } = useTranslation('common');

    if (file?.type?.startsWith('image/')) {
        return (
            <TransformWrapper
                smooth={true}
                initialScale={1}
                minScale={0.5}
                maxScale={5}
                wheel={{ step: 0.2 }}
                pinch={{ step: 5 }}
            >
                <TransformComponent>
                    <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className={cn(className)}
                    />
                </TransformComponent>
            </TransformWrapper>
        );
    }

    if (file?.type?.startsWith('video/')) {
        return (
            <div className={cn(className)}>
                <video
                    src={URL.createObjectURL(file)}
                    className="h-auto max-w-full"
                    controls
                />
            </div>
        );
    }

    return (
        <div className={cn(className)}>
            <div className="flex h-32 items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-gray-500">
                <span>{t('file.preview.notSupported')}</span>
            </div>
        </div>
    );
}

export type FileInputProps<TFileType extends FileTypes = FileTypes> = {
    accept?: string;
    type?: TFileType;
    /**
     * @deprecated use accept instead
     */
    subtype?: FileTypesSubtype<TFileType> | FileTypesSubtype<TFileType>[];
    minFileSize?: number;
    maxFileSize?: number;
    maxFileName?: number;
    name?: string;
    disabled?: boolean;
    loading?: boolean;
    multiple?: boolean;
    mode?: 'gallery' | 'camera' | 'both' | 'auto';
    /**
     * @deprecated Use `mode="camera"` instead. This prop is kept for backward compatibility.
     * When `mode` is provided, it takes precedence. If only `capture` is provided (legacy usage),
     * it will be converted to `mode="camera"`.
     */
    capture?: 'user' | 'environment';
    className?: string;
    onChange: (files: FileList) => void;
    onDragEnter?: (e: DragEvent<HTMLLabelElement>) => void;
    onDragLeave?: (e: DragEvent<HTMLLabelElement>) => void;
    onDragOver?: (e: DragEvent<HTMLLabelElement>) => void;
    onDrop?: (e: DragEvent<HTMLLabelElement>) => void;
};

export function FileInput<TFileType extends FileTypes = FileTypes>({
    accept: acceptProp,
    type,
    subtype = '*',
    minFileSize = 0,
    maxFileSize = 10 * 1024 * 1024, // 10MB
    maxFileName = 256,
    name,
    loading = false,
    disabled = loading,
    multiple,
    mode = 'both',
    capture,
    className,
    onChange,
    onDrop,
    onDragEnter,
    onDragLeave,
    onDragOver,
}: FileInputProps<TFileType>) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    // Derive effective mode from props
    const effectiveMode = useMemo(() => {
        if (mode)
            return mode;
        if (capture)
            return 'camera';
        return undefined;
    }, [mode, capture]);

    // Compute capture value for HTML attribute
    const captureValue = useMemo(() => {
        if (effectiveMode !== 'camera')
            return undefined;
        return capture ?? 'environment';
    }, [effectiveMode, capture]);

    const accept = useMemo(() => {
        const baseAccept = acceptProp ?? makeAcceptString({ subtype, type });

        // Apply Android workaround for 'both' and 'auto' modes
        if (effectiveMode === 'both' || effectiveMode === 'auto') {
            // Only apply workaround if we're on Android and accept includes image types
            if (isAndroid() && baseAccept.includes('image/')) {
                // Check if workaround is already applied
                if (!baseAccept.includes('android/allowCamera')) {
                    return `${baseAccept},android/allowCamera,capture=camera`;
                }
            }
        }

        return baseAccept;
    }, [acceptProp, subtype, type, effectiveMode]);

    // TODO: Add Translations
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files)
            return toast.error('Geen bestanden geselecteerd');
        if (!multiple && e.target.files.length > 1)
            return toast.error('Meerdere bestanden geselecteerd');
        if (Array.from(e.target.files).some(f => f.size > maxFileSize))
            return toast.error('Bestand is te groot');
        if (Array.from(e.target.files).some(f => f.size < minFileSize))
            return toast.error('Bestand is te klein');
        if (Array.from(e.target.files).some(f => f.name.length > maxFileName))
            return toast.error('Bestandsnaam is te lang');

        onChange(e.target.files);

        if (inputRef.current)
            inputRef.current.value = '';
    }

    function handleDragOver(e: React.DragEvent<HTMLLabelElement>) {
        e.preventDefault();
        e.stopPropagation();

        setDragging(true);
        if (onDragOver)
            onDragOver(e);
    }

    function handleDragEnter(e: React.DragEvent<HTMLLabelElement>) {
        e.preventDefault();
        e.stopPropagation();

        setDragging(true);
        if (onDragEnter)
            onDragEnter(e);
    }

    function handleDragLeave(e: React.DragEvent<HTMLLabelElement>) {
        e.preventDefault();
        e.stopPropagation();

        setDragging(false);
        if (onDragLeave)
            onDragLeave(e);
    }

    function handleDrop(e: React.DragEvent<HTMLLabelElement>) {
        e.preventDefault();
        e.stopPropagation();

        setDragging(false);
        if (onDrop)
            onDrop(e);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            onChange(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    }

    return (
        <label
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-disabled={disabled}
            className={cn(
                // Layout
                'flex w-full flex-col items-center justify-center gap-2 rounded-md',
                // Background & Border
                'border-box border border-input-border bg-transparent',
                // Text
                'px-3 py-2 text-sm',
                // Transition
                'transition duration-100',
                // Cursor
                'cursor-pointer',
                // Placeholder & Hover
                'placeholder:font-normal placeholder:text-muted-foreground hover:border-input-border-hover hover:shadow-input-hover',
                // Focus
                'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                {
                    'border-dashed border-primary': dragging,
                    'cursor-not-allowed opacity-50': disabled,
                },
                className,
            )}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={(e) => {
                if (disabled)
                    return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    inputRef.current?.click();
                }
            }}
        >
            <input
                ref={inputRef}
                type="file"
                className="hidden"
                name={name}
                accept={accept}
                multiple={multiple}
                onChange={handleChange}
                disabled={disabled}
                capture={captureValue}
            />
            <div className="pointer-events-none flex items-center justify-center rounded-lg border text-text-tertiary p-2">
                {loading
                    ? (
                            <Skeleton className="h-6 w-6 rounded-lg" />
                        )
                    : (
                            <CloudUpload size={24} />
                        )}
            </div>
            {loading
                ? (
                        <Skeleton className="pointer-events-none h-5 w-2/3 min-w-20" />
                    )
                : (
                        <p className="pointer-events-none flex flex-wrap items-center justify-center gap-1 text-sm">
                            <span className="text-primary">
                                Klik om te uploaden
                            </span>
                            <span className="text-text-tertiary">
                                of sleep en zet neer
                            </span>
                        </p>
                    )}
            {accept && (
                <FileAccept accept={accept} />
            )}
        </label>
    );
}

function FileAccept({ accept }: { accept: string }) {
    const types = useMemo(() => accept?.split(',')?.filter(x => !x.includes('android/allowCamera') || !x.includes('capture=camera'))?.map(type => type.split('/')[1]?.toUpperCase()), [accept]);

    return (
        <p className="pointer-events-none flex flex-wrap items-center justify-center gap-1 text-xs text-text-tertiary">
            {types.map((type, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`${type}-${idx}`}>
                    {type}
                    {idx < types.length - 2
                        ? ', '
                        : idx === types.length - 2
                            ? ` of `
                            : ''}
                </span>
            ))}
        </p>
    );
}
